import React from 'react';
import { componentLabel, recommendationTypeLabel } from '../appHelpers';

function formatPercent(value) {
  if (typeof value !== 'number') {
    return null;
  }
  return `${Math.round(value * 100)}%`;
}

function ChapterHeader({ kicker, title }) {
  return (
    <div className="chapter-header">
      <div>
        <p className="chapter-kicker">{kicker}</p>
        <h3>{title}</h3>
      </div>
    </div>
  );
}

function impactRangeLabel(impactEstimate) {
  if (!impactEstimate || impactEstimate.lower_bound === undefined || impactEstimate.lower_bound === null) {
    return '';
  }
  const upper = impactEstimate.upper_bound ?? impactEstimate.lower_bound;
  return upper === impactEstimate.lower_bound ? `${upper}` : `${impactEstimate.lower_bound} to ${upper}`;
}

function benefitNarrative(impactEstimate, recommendation) {
  const range = impactRangeLabel(impactEstimate);
  const componentName = componentLabel(recommendation.component);
  const scope = componentName ? ` in ${componentName}` : '';

  switch (impactEstimate?.unit) {
    case 'unguarded_actions_per_100_runs':
      return `We are detecting ${range} risky actions per 100 runs that can proceed without validation${scope}. Improving this could reduce unguarded execution and safeguard external tool or API actions before they run.`;
    case 'at_risk_runs_per_100':
      return `We are detecting ${range} at-risk runs per 100. Improving this could reduce unstable workflow outcomes and give reviewers a clearer signal before issues reach users.`;
    case 'at_risk_io_steps_per_100':
      return `We are detecting ${range} retrieval or external I/O steps per 100 that may be at risk. Improving this could reduce fragile dependency behavior and safeguard workflow handoffs.`;
    case 'seconds_per_run':
      return `We are detecting roughly ${range} extra seconds per run. Improving this could reduce latency and make the workflow feel more predictable.`;
    case 'tokens_per_run':
      return `We are detecting roughly ${range} extra tokens per run. Improving this could reduce model overhead while preserving the workflow outcome.`;
    case 'extra_steps_per_run':
    case 'extra_loop_steps_per_run':
      return `We are detecting roughly ${range} extra workflow steps per run. Improving this could reduce repeated work and make completion paths more direct.`;
    case 'human_handoffs_per_100_runs':
    case 'premature_handoffs_per_100_runs':
      return `We are detecting ${range} handoff-pressure cases per 100 runs. Improving this could reduce unnecessary escalation and preserve human review for the cases that need it.`;
    default:
      return recommendation.expected_effect || 'Improving this should reduce the detected runtime risk and make the workflow easier to validate in the next evidence window.';
  }
}

export default function RecommendationDetailView({ selectedSystem, recommendationDetail }) {
  const confidenceLabel = formatPercent(recommendationDetail.recommendation.confidence);
  const expectedBenefit = recommendationDetail.recommendation.expected_benefit?.impact_estimate;
  const expectedBenefitNarrative = expectedBenefit
    ? benefitNarrative(expectedBenefit, recommendationDetail.recommendation)
    : recommendationDetail.recommendation.expected_effect;

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel recommendation-detail-panel">
        <div className="chip-row recommendation-detail-nav">
          {recommendationDetail.finding ? (
            <a className="finding-link" href={`#/systems/${selectedSystem.id}/findings/${recommendationDetail.finding.id}`}>
              Back to finding
            </a>
          ) : null}
          <a
            className="finding-link"
            href={`#/systems/${selectedSystem.id}/architecture${
              recommendationDetail.recommendation.component_id ? `?component=${recommendationDetail.recommendation.component_id}` : ''
            }`}
          >
            Architecture
          </a>
          {recommendationDetail.recommendation.component_id ? (
            <a className="finding-link" href={`#/systems/${selectedSystem.id}/components/${recommendationDetail.recommendation.component_id}`}>
              Component: {componentLabel(recommendationDetail.recommendation.component)}
            </a>
          ) : null}
        </div>
        <p className="detail-kicker">{recommendationDetail.recommendation.priority} priority</p>
        <h2 className="recommendation-detail-title">{recommendationDetail.recommendation.title}</h2>
        <p className="panel-copy recommendation-detail-intro">
          {recommendationDetail.recommendation.description || recommendationDetail.recommendation.summary}
        </p>

        <div className="chip-row recommendation-detail-status-row">
          <span className="chip chip-emphasis">{recommendationTypeLabel(recommendationDetail.recommendation.recommendation_type)}</span>
          {confidenceLabel ? <span className="chip">{confidenceLabel} confidence in this recommendation</span> : null}
        </div>

        <div className="detail-summary-grid recommendation-detail-summary-grid">
          {recommendationDetail.recommendation.component ? (
            <a
              className="detail-card recommendation-detail-scope-card"
              href={`#/systems/${selectedSystem.id}/components/${recommendationDetail.recommendation.component.id}`}
            >
              <span className="summary-card-label">Affected component</span>
              <strong>{componentLabel(recommendationDetail.recommendation.component)}</strong>
              <p>Use the component detail view to inspect related findings and mapped workflow behavior.</p>
            </a>
          ) : null}
          {expectedBenefitNarrative ? (
            <div className="detail-card recommendation-detail-impact-card">
              <span className="summary-card-label">Expected benefit</span>
              <strong>Why this helps</strong>
              <p>{expectedBenefitNarrative}</p>
            </div>
          ) : null}
        </div>

        <section className="detail-section">
          <ChapterHeader kicker="Action" title="Suggested action" />
          <div className="detail-card recommendation-card recommendation-detail-primary-card">
            <strong>{recommendationDetail.recommendation.title}</strong>
            <p>{recommendationDetail.recommendation.summary}</p>
          </div>
        </section>

        <section className="detail-section">
          <ChapterHeader kicker="Guidance" title="Implementation guidance" />
          <div className="detail-summary-grid recommendation-detail-guidance-grid">
            <div className="detail-card recommendation-card recommendation-detail-support-card">
              <span className="summary-card-label">Example fix</span>
              <h3>Example fix</h3>
              <p>{recommendationDetail.recommendation.example_fix || recommendationDetail.recommendation.description || recommendationDetail.recommendation.summary}</p>
            </div>
            <div className="detail-card recommendation-card recommendation-detail-support-card">
              <span className="summary-card-label">Expected effect</span>
              <h3>Expected effect</h3>
              <p>
                {recommendationDetail.recommendation.expected_effect
                  || expectedBenefitNarrative
                  || 'Expected effect will appear here after the recommendation is regenerated.'}
              </p>
            </div>
            <div className="detail-card recommendation-card recommendation-detail-support-card recommendation-detail-confidence-card">
              <span className="summary-card-label">Confidence</span>
              <h3>Confidence</h3>
              <p>
                {confidenceLabel
                  ? `${confidenceLabel} confidence that this is the right next intervention for the detected finding. This is not a guarantee of the outcome; compare the next evidence window to confirm impact.`
                  : 'Confidence will appear here after the recommendation is regenerated.'}
              </p>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <ChapterHeader kicker="Context" title="Related context" />
          <div className="detail-summary-grid recommendation-detail-context-grid">
            {recommendationDetail.finding ? (
              <a className="detail-card recommendation-card recommendation-detail-context-card" href={`#/systems/${selectedSystem.id}/findings/${recommendationDetail.finding.id}`}>
                <span className="summary-card-label">Related finding</span>
                <strong>{recommendationDetail.finding.title}</strong>
                <p>{recommendationDetail.finding.customer_language_summary || recommendationDetail.finding.summary}</p>
              </a>
            ) : null}

            {recommendationDetail.recommendation.component ? (
              <a
                className="detail-card recommendation-card recommendation-detail-context-card"
                href={`#/systems/${selectedSystem.id}/components/${recommendationDetail.recommendation.component.id}`}
              >
                <span className="summary-card-label">Affected components</span>
                <strong>{componentLabel(recommendationDetail.recommendation.component)}</strong>
                <p>Affected component</p>
              </a>
            ) : (
              <div className="empty-state-card">
                <strong>No linked component yet</strong>
                <p>No component has been linked to this action yet.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </section>
  );
}
